import { FC, useEffect, useMemo } from 'react';
import { CircularProgress } from '@mui/material';
import { DoubleChargeInvoiceLineDto } from 'src/generated/services/TFinancialApi';
import { useDoubleChargeDetails } from 'src/use-cases/invoice/useDoubleChargeDetails';
import { DoubleChargeResolutionTable, DoubleChargeDecisions } from './DoubleChargeResolutionTable';

const parseDeviationStatus = (status?: string): boolean | undefined => {
  if (!status) return undefined;
  if (status.endsWith('_APPROVED')) return true;
  if (status.endsWith('_REJECTED')) return false;
  return undefined; // *_UNFILLED or unknown
};

interface IDoubleChargeResolutionWrapperProps {
  deviationId: string;
  decisions: DoubleChargeDecisions;
  onDecisionChange: (decisions: DoubleChargeDecisions) => void;
  disabled?: boolean;
  showValidationError?: boolean;
}

export const DoubleChargeResolutionWrapper: FC<IDoubleChargeResolutionWrapperProps> = ({
  deviationId,
  decisions,
  onDecisionChange,
  disabled = false,
  showValidationError = false,
}) => {
  const { data: doubleChargeDetail, isLoading, isError } = useDoubleChargeDetails(deviationId);

  const editableLines: DoubleChargeInvoiceLineDto[] = (doubleChargeDetail?.invoiceLines ?? []).filter((l) => l.editable === true);

  // Compute resolved decisions: merge parent decisions with deviationStatus from API.
  // This ensures read-only mode (with no-op onDecisionChange) still shows correct status.
  const resolvedDecisions = useMemo(() => {
    const merged: DoubleChargeDecisions = { ...decisions };
    editableLines.forEach((line) => {
      const key = line.deviationId ?? line.chargeId!;
      if (merged[key] === undefined) {
        merged[key] = parseDeviationStatus((line as any).deviationStatus);
      }
    });
    return merged;
  }, [decisions, editableLines]);

  // Propagate deviationStatus-derived decisions to parent state (for edit mode submit).
  // Only sets keys that haven't been touched yet (preserves user changes).
  useEffect(() => {
    if (!editableLines.length) return;
    const unsetKeys: DoubleChargeDecisions = {};
    let hasNew = false;
    editableLines.forEach((line) => {
      const key = line.deviationId ?? line.chargeId!;
      if (decisions[key] === undefined) {
        unsetKeys[key] = parseDeviationStatus((line as any).deviationStatus);
        hasNew = true;
      }
    });
    if (hasNew) {
      onDecisionChange({ ...decisions, ...unsetKeys });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doubleChargeDetail]);

  if (isLoading) {
    return (
      <div className='flex justify-center items-center py-16'>
        <CircularProgress size={24} color='primary' />
      </div>
    );
  }

  if (isError) {
    return (
      <div className='py-12 px-16 rounded-8 bg-red-50 border border-red-200 text-14 text-red-600'>Failed to load double charge details. Please try again.</div>
    );
  }

  if (editableLines.length < 1) {
    return null;
  }

  return (
    <div className='flex flex-col gap-12 pt-12'>
      <DoubleChargeResolutionTable
        charges={editableLines}
        decisions={resolvedDecisions}
        onDecisionChange={onDecisionChange}
        disabled={disabled}
        showValidationError={showValidationError}
      />
    </div>
  );
};
